import { useState, useEffect, useContext, ChangeEvent, FC, FormEvent } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  Text,
  VStack,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
  FormErrorMessage,
  useColorModeValue,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { LogoIcon } from '@bemap/ui';
import { login } from './login-service';
import { AuthContext } from '@bemap/utils';
import { useTranslation } from 'react-i18next';

interface LoginState {
  email: string;
  password: string;
  isLoading: boolean;
  isButtonDisabled: boolean;
  error: string;
  showPassword: boolean;
}

const Login: FC = () => {
  const [state, setState] = useState<LoginState>({
    email: '',
    password: '',
    isLoading: false,
    isButtonDisabled: true,
    error: '',
    showPassword: false,
  });

  const toast = useToast();
  const { signin } = useContext(AuthContext);
  const { t } = useTranslation();

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const subTextColor = useColorModeValue('gray.500', 'gray.400');
  const inputBgColor = useColorModeValue('gray.100', 'gray.700');
  const linkColor = useColorModeValue('blue.500', 'blue.300');
  // const termsTextColor = useColorModeValue('gray.400', 'gray.500');

  useEffect(() => {
    const isFormValid = state.email.trim() && state.password.trim();
    setState(prev => ({
      ...prev,
      isButtonDisabled: !isFormValid,
    }));
  }, [state.email, state.password]);

  const handleInputChange = (field: 'email' | 'password') => (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    setState(prev => ({
      ...prev,
      [field]: e.target.value,
      error: '',
    }));
  };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();

    if (state.isButtonDisabled || state.isLoading) return;

    setState(prev => ({
      ...prev,
      isLoading: true,
      error: '',
    }));

    try {
      const response = await login(state.email, state.password);

      if (response.statusCode === 200) {
        signin(response.message);

        toast({
          title: t('auth.successful_login'),
          description: t('auth.greeting'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

      } else {
        setState(prev => ({
          ...prev,
          error: response.message || t('auth.forgot_password_error'),
        }));

        toast({
          title: t('auth.error'),
          description: response.message || t('auth.forgot_password_error'),
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('auth.error');

      setState(prev => ({
        ...prev,
        error: errorMessage,
      }));

      toast({
        title: t('auth.error'),
        description: errorMessage,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setState(prev => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const togglePasswordVisibility = () => {
    setState(prev => ({
      ...prev,
      showPassword: !prev.showPassword,
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !state.isButtonDisabled) {
      handleLogin(e as any);
    }
  };

  return (
    <Flex minH="100vh" direction={{ base: 'column', md: 'row' }}>
      {/* Aside with background image */}
      <Box
        flex="1"
        display={{ base: 'none', md: 'block' }}
        bgGradient="linear(to-br, gray.100, gray.300)"
        position="relative"
        minH="100vh"
      >
        <Box position="absolute" top="6" left="8">
          <LogoIcon />
        </Box>
      </Box>
      <Flex flex="1" align="center" justify="center" bg={bgColor}>
        <Box w="full" maxW="md" p={8}>
          <Heading as="h2" size="lg" mb={2} textAlign="left" color={textColor}>
            {t('auth.greeting')}
          </Heading>
          <Text color={subTextColor} mb={6}>
            {t('auth.subheadline')}
          </Text>
          <form onSubmit={handleLogin}>
            <VStack spacing={4} align="stretch">
              <FormControl id="email" isRequired isInvalid={!!state.error}>
                <FormLabel>{t('auth.email_field_title')}</FormLabel>
                <Input
                  type="email"
                  placeholder={t('auth.email_field_placeholder')}
                  value={state.email}
                  onChange={handleInputChange('email')}
                  onKeyPress={handleKeyPress}
                  autoComplete="email"
                  bg={inputBgColor}
                  disabled={state.isLoading}
                />
              </FormControl>
              <FormControl id="password" isRequired isInvalid={!!state.error}>
                <FormLabel>{t('auth.password_field_title')}</FormLabel>
                <InputGroup>
                  <Input
                    type={state.showPassword ? 'text' : 'password'}
                    placeholder={t('auth.password_field_placeholder')}
                    value={state.password}
                    onChange={handleInputChange('password')}
                    onKeyPress={handleKeyPress}
                    autoComplete="current-password"
                    bg={inputBgColor}
                    disabled={state.isLoading}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={state.showPassword ? 'Hide password' : 'Show password'}
                      icon={state.showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={togglePasswordVisibility}
                      variant="ghost"
                      size="sm"
                      disabled={state.isLoading}
                    />
                  </InputRightElement>
                </InputGroup>
                {state.error && (
                  <FormErrorMessage>{state.error}</FormErrorMessage>
                )}
              </FormControl>
              <Button
                type="submit"
                colorScheme="blue"
                size="md"
                w="full"
                isLoading={state.isLoading}
                loadingText={t('general.loading')}
                disabled={state.isButtonDisabled || state.isLoading}
              >
                {t('auth.login')}
              </Button>
            </VStack>
          </form>
          <Text fontSize="sm" color="gray.600" mt={4} textAlign="center">
            <Link href="/reset-password" color={linkColor} textDecor="underline">
              {t('auth.forgot_password')}
            </Link>
          </Text>
          {/* <Text fontSize="xs" color={termsTextColor} mt={8} textAlign="center">
            By clicking continue, you agree to our{' '}
            <Link href="#" color={linkColor} textDecor="underline">Terms of Service</Link> and{' '}
            <Link href="#" color={linkColor} textDecor="underline">Privacy Policy</Link>.
          </Text> */}
        </Box>
      </Flex>
    </Flex>
  );
};

export default Login; 